<style lang="scss">
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/colors";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/utils";
  @use "@sourcewhale/shared/svelte-design-system/assets/css/design-system/mixins";
  button,
  a {
    display: flex;
    align-items: center;
    border: solid 1px transparent;
    cursor: pointer;
    text-align: center;
    user-select: none;
    justify-content: center;
    &.trailing-icon {
      > span.button-content {
        margin-right: auto;
      }
    }
    > span.button-content {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
    }
    @mixin disabledInner() {
      @include colors.background-color(interfaceDisabledGrey_10);
      @include colors.color(textDisabledGrey);
      border: solid 1px colors.get(interfaceSwGrey_10);
      outline: none;
      cursor: auto;
    }
    @mixin disabled() {
      &.disabled {
        @include disabledInner();
        &:focus,
        &:hover,
        &:active,
        &.active {
          @include disabledInner();
        }
      }
    }
    @mixin disabledGhostInner() {
      @include colors.color(textDisabledGrey);
      outline: none;
      cursor: auto;
    }
    @mixin disabledGhost() {
      &.disabled {
        @include disabledGhostInner();
        &:focus,
        &:hover,
        &:active,
        &.active {
          @include disabledGhostInner();
        }
      }
    }
    /* ------------- */
    /* STYLE CLASSES */
    /* ------------- */
    &.primary {
      border: solid 1px colors.get(primary_50);
      @include colors.background-color(primary_50);
      @include colors.color(textWhite);
      @include disabled();
      &:hover {
        @include colors.background-color(primary_60);
      }
      &:focus-visible {
        @include colors.background-color(primary_60);
        outline: 2px solid colors.get(primary_40);
      }
      &:active,
      &.active {
        @include colors.background-color(primary_80);
      }
    }
    &.secondary {
      @include colors.background-color(primary_10);
      @include colors.color(primary_70);
      border: solid 1px transparent;
      @include disabled();
      &:hover {
        @include colors.background-color(primary_20);
        @include colors.color(primary_70);
      }
      &:focus-visible {
        @include colors.background-color(primary_20);
        @include colors.color(primary_70);
        outline: 2px solid colors.get(primary_40);
      }
      &:active,
      &.active {
        @include colors.background-color(primary_30);
        @include colors.color(primary_80);
      }
    }
    &.secondary-outlined {
      @extend .secondary;
      @include disabled();
      &:hover {
        @include colors.background-color(primary_20);
        @include colors.color(primary_70);
        outline: 2px solid colors.get(primary_30); // TODO - check figma 1.5px not possible
      }
    }
    &.tertiary {
      @include colors.background-color(textWhite);
      @include colors.color(primary_50);
      border: solid 1px colors.get(interfaceSwGrey_10);
      @include disabled();
      &:hover {
        @include colors.background-color(interfaceSwGrey_5);
        @include colors.color(primary_60);
      }
      &:focus-visible {
        @include colors.background-color(interfaceSwGrey_5);
        @include colors.color(primary_60);
        outline: 2px solid colors.get(primary_50);
      }
      &:active,
      &.active {
        @include colors.background-color(primary_5);
        @include colors.color(primary_60);
      }
    }
    &.tertiary-grey {
      @include colors.background-color(textWhite);
      @include colors.color(textLighterBlack);
      border: solid 1px colors.get(interfaceSwGrey_10);
      @include disabled();
      &:hover {
        @include colors.background-color(interfaceSwGrey_5);
        @include colors.color(textBlack);
      }
      &:focus-visible {
        @include colors.background-color(interfaceSwGrey_5);
        @include colors.color(textBlack);
        outline: 2px solid colors.get(primary_40);
      }
      &:active,
      &.active {
        @include colors.background-color(interfaceSwGrey_10);
        @include colors.color(textBlack);
      }
    }
    &.tertiary-blue {
      @include colors.background-color(textWhite);
      @include colors.color(primary_60);
      border: solid 1.4px colors.get(interfaceSwGrey_10);
      @include disabled();
      &:hover {
        @include colors.background-color(interfaceSwGrey_5);
        @include colors.color(primary_60);
      }
      &:focus-visible {
        @include colors.background-color(interfaceSwGrey_5);
        @include colors.color(primary_50);
        outline: 2px solid colors.get(primary_50);
      }
      &:active,
      &.active {
        @include colors.background-color(primary_5);
        @include colors.color(primary_60);
      }
    }
    &.ghost-blue {
      background-color: transparent;
      @include colors.color(primary_50);
      @include disabledGhost();
      &:hover {
        @include colors.color(primary_60);
      }
      &:focus-visible {
        @include colors.color(primary_60);
        outline: 2px solid colors.get(primary_40);
      }
      &:active,
      &.active {
        @include colors.background-color(primary_10);
        @include colors.color(primary_60);
      }
    }
    &.ghost-grey {
      background-color: transparent;
      @include colors.color(textLighterBlack);
      @include disabledGhost();
      &:hover {
        @include colors.color(primary_50);
      }
      &:focus-visible {
        @include colors.color(primary_60);
        outline: 2px solid colors.get(primary_40);
      }
      &:active,
      &.active {
        @include colors.background-color(interfaceSwGrey_10);
        @include colors.color(textBlack);
      }
    }
    &.destructive {
      background-color: transparent;
      @include colors.color(systemError_100);
      &:focus {
        outline: 2px solid colors.get(primary_60);
      }
      &:hover {
        @include colors.background-color(systemError_10);
        @include colors.color(systemError_100);
      }
    }
    &.positive {
      @include colors.background-color(systemPositive_10);
      @include colors.color(systemPositive_100);
      border: solid 1px transparent;
      @include disabled();
      &:hover {
        @include colors.background-color(systemPositive_10);
        @include colors.color(systemPositive_100);
        outline: 1.5px solid colors.get(systemPositive_100);
      }
      &:focus-visible {
        @include colors.background-color(systemPositive_10);
        @include colors.color(systemPositive_100);
        outline: 2px solid colors.get(primary_50);
      }
      &:active,
      &.active {
        @include colors.background-color(systemPositive_100);
        @include colors.color(systemPositive_10);
      }
    }
    &.destructive {
      @include colors.background-color(systemError_10);
      @include colors.color(systemError_100);
      border: solid 1px transparent;
      @include disabled();
      &:hover {
        @include colors.background-color(systemError_10);
        @include colors.color(systemError_100);
        outline: 1.5px solid colors.get(systemError_100);
      }
      &:focus-visible {
        @include colors.background-color(systemError_10);
        @include colors.color(systemError_100);
        outline: 2px solid colors.get(primary_50);
      }
      &:active,
      &.active {
        @include colors.background-color(systemError_100);
        @include colors.color(systemError_10);
      }
    }
    &.ai {
      @include disabled();
      @include colors.color(textBlack);
      border: solid 1px transparent;
      background:
        linear-gradient(white, white) padding-box,
        colors.get(aiGradient) border-box;
      @mixin ai-button-content {
        color: transparent;
        background: colors.get(aiGradient);
        background-clip: text;
      }
      &:hover:not(:disabled),
      &:active,
      &.active:not(:disabled) {
        > .button-content {
          @include ai-button-content();
        }
      }
      &:focus-visible {
        border: solid 1px colors.get(primary_50);
        > .button-content {
          @include ai-button-content();
        }
      }
    }
    &.ai-secondary {
      @include disabled();
      @include colors.background-color(aiCtaPurple);
      @include colors.color(aiIconPurple);
      > .button-content {
        @include colors.color(textBlack);
        @include mixins.button3();
      }
      &:hover {
        @include colors.background-color(aiHoverPurple);
      }
      &:focus-visible {
        @include colors.background-color(aiHoverPurple);
        @include colors.color(aiTextPurple);
        outline: 2px solid colors.get(aiIconPurple);
      }
      &:active,
      &.active {
        @include colors.background-color(aiHoverPurple);
        @include colors.color(aiTextPurple);
      }
    }
    /* ------------ */
    /* SIZE CLASSES */
    /* ------------ */
    // This is not yet used in the design system. It's designed to be the same
    // size as the StatusTag and is needed for the Companies Modal page.
    &.x-small {
      height: utils.in_rem(32px);
      padding: 0 utils.in_rem(8px);
      gap: utils.in_rem(4px);
      border-radius: 8px;
      > span.button-content {
        @include mixins.body4();
      }
    }
    &.ai-emphasis {
      border: solid 1.4px colors.get(aiIconBlue);
      background: colors.get(aiGradient);
      color: colors.get(textWhite);
      background-scale: 1.1;
      &:hover {
        border: solid 1.4px colors.get(aiIconPurple);
      }
      &:focus-visible {
        outline: 2px solid colors.get(primary_50);
      }
      &:active,
      &.active {
        outline: 2px solid colors.get(primary_50);
      }
    }
    &.ai-solid-primary {
      @include colors.background-color(aiIconPurple);
      @include colors.color(textWhite);
      @include disabled();
      &:hover {
        @include colors.background-color(aiTextPurple);
      }
      &:focus-visible {
        @include colors.background-color(aiTextPurple);
        outline: 2px solid colors.get(primary_50);
      }
      &:active,
      &.active {
        border: 1.4px solid colors.get(primary_50);
      }
    }
    &.upgrade {
      @include colors.background-color(accentUpgrade);
      @include colors.color(textWhite);
      @include disabled();
      &:hover {
        @include colors.background-color(accentTrialSecondaryPink);
      }
      &:focus-visible {
        @include colors.background-color(accentTrialSecondaryPink);
        outline: 2px solid colors.get(primary_50);
      }
      &:active,
      &.active {
        @include colors.background-color(accentTrialPink);
        @include colors.color(primary_80);
        border: 1.4px solid colors.get(accentTrialPink);
      }
    }
    &.upgrade-secondary {
      @include colors.background-color(interfaceWhite);
      @include colors.color(accentUpgrade);
      border: 1.4px solid colors.get(accentTrialPink);
      @include disabled();
      &:hover {
        @include colors.background-color(accentUpgradeSecondary);
      }
      &:focus {
        @include colors.color(accentUpgradeSecondary);
        outline: 2px solid colors.get(primary_50);
      }
      &:active,
      &.active {
        @include colors.background-color(accentTrialPink);
        @include colors.color(primary_80);
      }
    }
    /// SIZE CLASSES
    &.xs {
      height: utils.in_rem(32px);
      padding: 0 utils.in_rem(8px);
      gap: utils.in_rem(8px);
      border-radius: 8px;
      > span.button-content {
        @include mixins.button-small();
      }
    }
    &.xsmall {
      height: utils.in_rem(32px);
      padding: 0 utils.in_rem(8px);
      gap: utils.in_rem(8px);
      border-radius: 8px;
      > span.button-content {
        @include mixins.button3();
      }
    }
    &.small {
      height: utils.in_rem(36px);
      padding: 0 utils.in_rem(12px);
      gap: utils.in_rem(8px);
      border-radius: 8px;
      > span.button-content {
        @include mixins.button-small();
      }
    }
    &.large {
      height: utils.in_rem(44px);
      padding: 0 utils.in_rem(20px);
      gap: utils.in_rem(12px);
      border-radius: 10px;
      > span.button-content {
        @include mixins.button();
      }
    }
    // If there's no non-empty label (.button-content), remove the inter-child gap.
    // Prevents unwanted spacing between icons in icon-only buttons or when the label slot is empty.
    &:not(:has(span.button-content:not(:empty))) {
      gap: 0;
    }
    &.full-width {
      width: 100%;
    }
    &.input-field {
      @include colors.background-color(textWhite);
      @include colors.color(textBlack);
      @include disabled();
      border: solid 1px colors.get(interfaceSwGrey_10);
      &:hover {
        @include colors.background-color(interfaceSwGrey_10);
      }
      &:focus-visible {
        outline: 2px solid colors.get(primary_40);
      }
      &:active,
      &.active {
        outline: 2px solid colors.get(primary_40);
      }
    }
    /* --------------------------- */
    /* SIZE CLASSES (INPUT FIELDS) */
    /* --------------------------- */
    &.xsmall-input-field {
      height: utils.in_rem(32px);
      padding: 0 utils.in_rem(8px);
      gap: utils.in_rem(8px);
      border-radius: 8px;
      > span.button-content {
        @include mixins.button3();
      }
    }
    &.small-input-field {
      height: utils.in_rem(36px);
      padding: 0 utils.in_rem(12px);
      gap: utils.in_rem(8px);
      border-radius: 8px;
      > span.button-content {
        @include mixins.body2();
      }
    }
    &.large-input-field {
      height: utils.in_rem(44px);
      padding: 0 utils.in_rem(12px);
      gap: utils.in_rem(12px);
      border-radius: 10px;
      > span.button-content {
        @include mixins.body2();
      }
    }
    // SIZE CLASSES FOR CANDIDATE CTAS
    &.candidate-cta {
      height: utils.in_rem(44px);
      width: utils.in_rem(44px);
      gap: utils.in_rem(12px);
      padding: 0;
      border-radius: 10px;
    }
  }
</style>

<script>
  import { showToast, ToastError } from "@sourcewhale/core";
  import Icon from "./Icon.svelte";
  import Spinner from "./Spinner.svelte";
  /**
   * Note to developers: "disabled" styling is deliberately decoupled from the
   * disabled attribute. Do not disable pointer events on the disabled class,
   * instead actually disable the button with a disabled prop.
   */
  export let kind = "primary";
  export let size = "small";
  export let leadingIcon = "";
  export let trailingIcon = "";
  export let disabled = false;
  export let style = "";
  let click = () => {};
  let mouseenter = () => {};
  let mouseleave = () => {};
  let focus = () => {};
  let blur = () => {};
  let processing = false;
  const wrappedClick = (e) => {
    if (!click) return;
    const ret = click(e);
    if (ret instanceof Promise) {
      processing = true;
      ret
        .catch((err) => {
          if (err instanceof ToastError) {
            showToast(err);
          } else {
            throw err;
          }
        })
        .finally(() => {
          processing = false;
        });
    }
  };
  $: wrappedDisabled = processing || disabled;
  $: wrappedLeadingIcon = processing ? "Spinner" : leadingIcon;
  let cls = "";
  export {
    cls as class,
    click as onclick,
    mouseenter as onmouseenter,
    mouseleave as onmouseleave,
    focus as onfocus,
    blur as onblur,
  };
  // bug in svelte 5? Not exporting directly makes this non-reactive
  export let href = "";
  export let el;
  // Dropdown inputs have smaller icons in the large state
  const iconSize = kind === "input-field" ? "small" : size;
</script>

{#if href}
  <a
    {style}
    {href}
    bind:this={el}
    class="{kind} {size} {cls}"
    class:trailing-icon={trailingIcon}
    on:click={wrappedClick}
    on:mouseenter={mouseenter}
    on:mouseleave={mouseleave}
    on:focus={focus}
    on:blur={blur}
    disabled={wrappedDisabled}
    {...$$restProps}
  >
    {#if wrappedLeadingIcon}
      <Icon class={wrappedLeadingIcon} {size} {...$$restProps} />
    {/if}
    {#if $$slots.default}
      <span class="button-content"><slot /></span>
    {/if}
    {#if trailingIcon}
      <Icon class={trailingIcon} {size} {...$$restProps} />
    {/if}
  </a>
{:else}
  <button
    {style}
    bind:this={el}
    class="{kind} {kind === 'input-field' ? `${size}-${kind}` : size} {cls}"
    class:trailing-icon={trailingIcon}
    on:click={wrappedClick}
    on:mouseenter={mouseenter}
    on:mouseleave={mouseleave}
    on:focus={focus}
    on:blur={blur}
    disabled={wrappedDisabled}
    {...$$restProps}
  >
    {#if wrappedLeadingIcon.split(" ").includes("Spinner")}
      <Spinner class={wrappedLeadingIcon} size={iconSize} />
    {:else if wrappedLeadingIcon}
      <Icon class={wrappedLeadingIcon} size={iconSize} {...$$restProps} />
    {/if}
    {#if $$slots.default}
      <span class="button-content"><slot /></span>
    {/if}
    {#if trailingIcon}
      <Icon class={trailingIcon} size={iconSize} {...$$restProps} />
    {/if}
  </button>
{/if}
